import { z } from 'zod';
import { LEVELS, ROLES, CATEGORIES, define, DEFINITION_KINDS } from '../types.js';

/**
 * Routing preferences configuration for lane enrichment and route computation.
 *
 * Controls how routes are calculated during lane enrichment, including
 * toll avoidance, highway avoidance, ferry avoidance, and route optimization
 * preferences.
 *
 * @remarks
 * These settings are applied during lane enrichment when computing routes
 * between pickup and delivery locations. The settings follow the standard
 * config hierarchy and can be overridden at multiple levels.
 *
 * **When to use:**
 * - Configure toll avoidance for customers who want to minimize toll costs
 * - Set highway avoidance for customers with vehicle restrictions
 * - Set ferry avoidance for customers who cannot use ferry routes
 * - Choose between fastest (time-optimized) or shortest (distance-optimized) routes
 *
 * **Behavior:**
 * When lane enrichment occurs, these settings are fetched from the config hierarchy
 * and applied to the Google Routes API request. The settings used are stored in
 * the lane's `creation_criteria` field for re-enrichment consistency.
 */
export const schema = z
  .object({
    avoid_tolls: z
      .boolean()
      .describe(
        'When true, routes will avoid toll roads where possible. This may result in longer travel times or distances.'
      )
      .optional(),
    avoid_highways: z
      .boolean()
      .describe(
        'When true, routes will avoid highways where possible. Useful for vehicles with highway restrictions or customers preferring local roads.'
      )
      .optional(),
    avoid_ferries: z
      .boolean()
      .describe(
        'When true, routes will avoid ferries where possible. Useful for customers who cannot use ferry routes.'
      )
      .optional(),
    route_preference: z
      .enum(['fastest', 'shortest'])
      .describe(
        "Route optimization preference. 'fastest' optimizes for travel time (default), 'shortest' optimizes for distance."
      )
      .optional(),
  })
  .strict()
  .describe(
    'Routing preferences for lane enrichment. Controls toll avoidance, highway avoidance, ferry avoidance, and route optimization preferences.'
  );

export type Routing = z.infer<typeof schema>;

export const definition = define({
  key: 'routing',
  schema,
  label: 'Routing',
  description:
    'Routing preferences for lane enrichment. Controls toll avoidance, highway avoidance, ferry avoidance, and route optimization preferences.',
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.MOVE_PLANNING.key,
  writableAtLevels: [
    LEVELS.HOPDRIVE,
    LEVELS.REGION,
    LEVELS.ORGANIZATION,
    LEVELS.CUSTOMER,
    LEVELS.WORKFLOWSET,
    LEVELS.RATERULEGROUP,
  ],
  readableByRoles: [ROLES.ADMIN],
  defaultValue: {
    avoid_tolls: false,
    avoid_highways: false,
    avoid_ferries: false,
    route_preference: 'fastest',
  },
});
